// src/pages/Users/News/components/NewsFeaturedCarousel.jsx

import React, { useEffect, useMemo, useState } from "react";
import createDOMPurify from "dompurify";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaExpandAlt,
  FaNewspaper,
  FaUserEdit,
} from "react-icons/fa";

import NewsImageModal from "./NewsImageModal";

import {
  FALLBACK_NEWS_IMG,
  formatNewsDate,
  getAuthorName,
  getNewsCover,
  getNewsDate,
} from "../utils/newsUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et gestion sécurisée des styles enrichis
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes du contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "news-featured-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-base leading-8 text-slate-600",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes et retours à la ligne
  "prose-p:my-4",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-4",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-orange-600",
  "hover:prose-a:underline",
  "dark:prose-a:text-orange-300",

  // Listes à puces
  "[&_ul]:!my-5",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-7",

  // Listes numérotées
  "[&_ol]:!my-5",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-7",

  // Éléments des listes
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes à puces
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-6",
  "[&_ul_ul_ul]:!list-[square]",

  // Sous-listes numérotées
  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-6",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-sm",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-5",
  "prose-blockquote:border-orange-500",
  "prose-blockquote:bg-orange-50",
  "prose-blockquote:px-5",
  "prose-blockquote:py-3",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-orange-500/10",

  // Images intégrées
  "prose-img:mx-auto",
  "prose-img:rounded-xl",
  "prose-img:shadow-md",

  // Séparateurs et code
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Marges du premier et dernier élément
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

const stripHtml = (html = "") => {
  if (typeof document === "undefined") {
    return String(html || "").replace(/<[^>]*>/g, "").trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
};

const sanitizeHtml = (html = "") => {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
};

function onImgError(event) {
  const image = event?.currentTarget;

  if (!image) return;

  if (image.dataset.fallbackApplied === "true") return;

  image.dataset.fallbackApplied = "true";
  image.src = FALLBACK_NEWS_IMG;
}

/* -------------------------------------------------------
   Composant
-------------------------------------------------------- */

export default function NewsFeaturedCarousel({ items = [] }) {
  const [index, setIndex] = useState(0);
  const [imageOpen, setImageOpen] = useState(false);

  const safeItems = useMemo(() => {
    return Array.isArray(items)
      ? items.filter(Boolean)
      : [];
  }, [items]);

  const current = safeItems[index] || safeItems[0];

  useEffect(() => {
    if (index > safeItems.length - 1) {
      setIndex(0);
    }
  }, [index, safeItems.length]);

  /*
   * Le HTML est sécurisé tout en conservant les styles autorisés.
   */
  const contentHtml = useMemo(() => {
    const rawContent = current?.content || "";
    const plainContent = stripHtml(rawContent);

    if (!plainContent) {
      return sanitizeHtml(
        '<p style="font-style: italic;">Contenu non renseigné.</p>',
      );
    }

    return sanitizeHtml(rawContent);
  }, [current?.content]);

  if (!current) return null;

  const cover = getNewsCover(current);
  const author = getAuthorName(current);
  const date = formatNewsDate(getNewsDate(current));

  const previous = () => {
    setIndex(
      (previousIndex) =>
        (previousIndex - 1 + safeItems.length) %
        safeItems.length,
    );
  };

  const next = () => {
    setIndex(
      (previousIndex) =>
        (previousIndex + 1) % safeItems.length,
    );
  };

  const selectNews = (nextIndex) => {
    setIndex(nextIndex);
  };

  return (
    <>
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-950/5 dark:border-slate-800 dark:bg-slate-900">
        <div className="grid min-h-[520px] lg:grid-cols-[0.95fr_1.05fr]">
          {/* Image de l’actualité */}

          <div className="relative min-h-[340px] overflow-hidden bg-slate-950 lg:min-h-[520px]">
            <img
              src={cover}
              alt={current?.title || "Actualité"}
              className="h-full w-full object-cover"
              loading="eager"
              onError={onImgError}
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/15 to-transparent" />

            <button
              type="button"
              onClick={() => setImageOpen(true)}
              className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105 active:scale-95"
              aria-label="Agrandir la photo"
              title="Agrandir la photo"
            >
              <FaExpandAlt />
            </button>

            {safeItems.length > 1 ? (
              <button
                type="button"
                onClick={previous}
                className="absolute left-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-950 shadow-lg transition hover:scale-105 active:scale-95"
                aria-label="Actualité précédente"
              >
                <FaArrowLeft />
              </button>
            ) : null}

            <div className="absolute bottom-5 left-5 right-5">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white backdrop-blur">
                <FaUserEdit />
                {author}
              </div>

              <h2 className="text-3xl font-bold leading-tight text-white sm:text-4xl">
                {current?.title || "Actualité du club"}
              </h2>
            </div>
          </div>

          {/* Contenu de l’actualité */}

          <div className="relative flex min-h-0 flex-col p-5 sm:p-7 lg:p-9">
            <div className="flex items-start justify-between gap-4">
              <span className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-orange-600 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-orange-300">
                <FaNewspaper />
                Article du club
              </span>

              <span className="rounded-full bg-white px-4 py-2 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700">
                {index + 1} / {safeItems.length}
              </span>
            </div>

            <h3 className="mt-7 text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl">
              {current?.title || "Actualité du club"}
            </h3>

            <div className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-slate-50 px-3 py-2 text-xs font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-800">
              <FaCalendarAlt />
              {date}
            </div>

            {/* Contenu HTML enrichi et scrollable */}

            <div
              className={cn(
                RICH_TEXT_CLASSES,
                "mt-5 max-h-[230px] overflow-y-auto overflow-x-hidden pr-3",
                "scrollbar-thin scrollbar-track-transparent",
                "scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-700",
              )}
              style={{
                tabSize: 4,
                WebkitOverflowScrolling: "touch",
              }}
              dangerouslySetInnerHTML={{
                __html: contentHtml,
              }}
            />

            {/* Navigation */}

            <div className="mt-auto pt-7">
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  {safeItems.map((item, dotIndex) => (
                    <button
                      key={item.id || dotIndex}
                      type="button"
                      onClick={() => selectNews(dotIndex)}
                      className={cn(
                        "h-2.5 rounded-full transition",
                        dotIndex === index
                          ? "w-8 bg-orange-500"
                          : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600",
                      )}
                      aria-label={`Afficher l’actualité ${
                        dotIndex + 1
                      }`}
                      aria-current={
                        dotIndex === index ? "true" : undefined
                      }
                    />
                  ))}
                </div>

                {safeItems.length > 1 ? (
                  <button
                    type="button"
                    onClick={next}
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg ring-1 ring-slate-200 transition hover:scale-105 active:scale-95 dark:bg-slate-950 dark:text-white dark:ring-slate-700"
                    aria-label="Actualité suivante"
                  >
                    <FaArrowRight />
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsImageModal
        isOpen={imageOpen}
        imageUrl={cover}
        title={current?.title}
        onClose={() => setImageOpen(false)}
      />
    </>
  );
}